import {ToCamel, toCamel, toSnake} from 'ts-case-convert/lib/caseConvert';

import {TablePagination} from '@veneer/core';

import {OrderingParam, SortBy} from './requests';

export function parseFilters<F>(
    params: URLSearchParams,
    transforms: {[K in keyof Required<F>]: (params: URLSearchParams, field: string) => F[K]},
    defaults: F
): F {
    const fields = Object.keys(transforms) as (keyof F & string)[];

    return fields.reduce(
        (acc, field) => {
            const val = transforms[field](params, field);
            if (val !== undefined) {
                acc[field] = val;
            }
            return acc;
        },
        {...defaults}
    );
}

export function parseString(params: URLSearchParams, field: string): string | undefined {
    return params.get(field) || undefined;
}

export function parseMultiString(params: URLSearchParams, field: string): string[] | undefined {
    return params.has(field) ? params.getAll(field) : undefined;
}

export function parseNumber(params: URLSearchParams, field: string): number | undefined {
    const string = params.get(field);
    if (string === null) {
        return undefined;
    }
    const value = parseInt(string, 10);
    if (isNaN(value)) {
        return undefined;
    }
    return value;
}

export function parseDate(params: URLSearchParams, field: string): Date | undefined {
    const dateString = params.get(field);
    if (dateString === null) {
        return undefined;
    }
    const date = new Date(dateString);
    return isNaN(date.valueOf()) ? undefined : date;
}

function isEnum<E extends string | number>(enumVal: E, enumType: Record<string, E>): enumVal is E {
    return Object.values(enumType).includes(enumVal);
}

function parseEnum<E extends string | number>(
    enumVal: string | number | null,
    enumType: Record<string, E>
): E | undefined {
    if (enumVal === null) {
        return undefined;
    }

    if (isEnum(enumVal, enumType)) {
        return enumVal as E;
    }
    return undefined;
}

export function parseStrEnum<E extends string>(
    enumType: Record<string, E>
): (params: URLSearchParams, field: string) => E | undefined {
    return (params, field) => {
        return parseEnum(params.get(field), enumType);
    };
}

export function parseIntEnum<E extends number>(
    enumType: Record<string, E>
): (params: URLSearchParams, field: string) => E | undefined {
    return (params, field) => {
        return parseEnum(parseNumber(params, field) ?? null, enumType);
    };
}

export function parseBool(params: URLSearchParams, field: string): boolean | undefined {
    const boolString = params.get(field);
    if (boolString === null) {
        return undefined;
    }
    return boolString === true.toString();
}

export function parseSort<O extends string>(params: URLSearchParams, defaults?: SortBy<O>): SortBy<O> | undefined {
    const ordering = params.get('ordering') as OrderingParam<O>;
    if (ordering === null) {
        return defaults;
    }

    return {
        type: ordering.startsWith('-') ? 'descending' : 'ascending',
        id: (ordering.startsWith('-') ? toCamel(ordering.slice(1)) : toCamel(ordering)) as ToCamel<O>,
    };
}

export function parsePagination(params: URLSearchParams): Pick<Required<TablePagination>, 'pageSize' | 'currentPage'> {
    return {
        pageSize: parseNumber(params, 'pageSize') || 50,
        currentPage: parseNumber(params, 'page') || 1,
    };
}

export function serialiseSortBy<O extends string>(sortBy?: SortBy<O>): OrderingParam<O> | undefined {
    if (!sortBy) {
        return undefined;
    }

    const ordering = toSnake(sortBy.id) as O;

    return sortBy.type === 'ascending' ? ordering : `-${ordering}`;
}

type SerializableQueryParam = Date | string | number | boolean;

function serializeQueryParam(param: SerializableQueryParam): string {
    if (param instanceof Date) {
        return param.toISOString().slice(0, 10);
    } else {
        return param.toString();
    }
}

export function serialiseQueryParams(
    queryParams: Record<string, SerializableQueryParam | SerializableQueryParam[]>
): URLSearchParams {
    const searchParams = new URLSearchParams();
    Object.entries(queryParams)
        .filter(([, v]) => v !== undefined)
        .forEach(([k, v]) => {
            if (Array.isArray(v)) {
                v.forEach(vv => searchParams.append(k, serializeQueryParam(vv)));
            } else {
                searchParams.append(k, serializeQueryParam(v));
            }
        });
    return searchParams;
}
